import React, { useState, useEffect, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    TableRow,
    TableCell,
} from '@mui/material';
import {
    PlayArrow,
    SolarPower,
    EvStation,
    AcUnit,
    FiberManualRecord,
} from '@mui/icons-material';
import {
    vortxColors,
    retroColors,
    vortxTypography,
    ProjectTableContainer,
    ProjectTableHead,
    ProjectTableHeaderCell,
    ProjectTableBody,
} from '@vortx/shared-ui';

const DURATION_OPTIONS = [
    { value: '1w', label: '1 Week', days: 7 },
    { value: '1m', label: '1 Month', days: 30 },
    { value: '1y', label: '1 Year', days: 365 },
];

const SPEED_PRESETS = [
    { value: 1, label: 'Real-time (1x)' },
    { value: 10, label: 'Fast (10x)' },
    { value: 100, label: 'Turbo (100x)' },
];

/** Month names for the start period picker */
const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
];

/** Generate week options for the year (Week 1 = Jan 1-7, etc.) */
function getWeekOptions() {
    const weeks = [];
    for (let w = 0; w < 52; w++) {
        const startDay = w * 7 + 1;
        const date = new Date(2026, 0, startDay);
        const label = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        weeks.push({ value: w, label: `Week of ${label}`, startStep: w * 7 * 96 });
    }
    return weeks;
}
const WEEK_OPTIONS = getWeekOptions();

/** App display metadata */
const APP_CONFIG = {
    site_dna: { label: 'Solar + Storage', Icon: SolarPower },
    flux_ev: { label: 'EV Fleet', Icon: EvStation },
    flux_cold: { label: 'Cold Storage', Icon: AcUnit },
};

/** Ordered app keys for consistent display */
const APP_ORDER = ['site_dna', 'flux_ev', 'flux_cold'];

const fieldSx = {
    '& .MuiInputBase-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.8rem',
        color: vortxColors.white,
        backgroundColor: retroColors.input,
    },
    '& .MuiInputLabel-root': {
        fontFamily: vortxTypography.mono,
        fontSize: '0.75rem',
        color: retroColors.muted,
    },
    '& .MuiOutlinedInput-notchedOutline': {
        borderColor: `${vortxColors.teal}66`,
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: vortxColors.teal,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: vortxColors.gold,
    },
};

const menuPaperSx = {
    backgroundColor: retroColors.surface,
    border: `1px solid ${vortxColors.teal}44`,
};

const menuItemSx = {
    fontFamily: vortxTypography.mono,
    fontSize: '0.8rem',
    color: vortxColors.white,
    '&:hover': { backgroundColor: `${vortxColors.teal}22` },
    '&.Mui-selected': { backgroundColor: `${vortxColors.gold}22` },
};

/**
 * AppScenarioSelector - Scenario dropdown for a single app domain.
 */
function AppScenarioSelector({ appKey, appData, selectedId, onChange, disabled }) {
    const config = APP_CONFIG[appKey];
    if (!config) return null;

    const { label, Icon } = config;
    const isEnabled = appData?.enabled && appData?.scenarios?.length > 0;
    const isDisabledState = disabled || !isEnabled;

    return (
        <Box sx={{
            mb: 2,
            opacity: isDisabledState ? 0.4 : 1,
            transition: 'opacity 0.2s ease',
        }}>
            {/* App label row */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.8,
                mb: 0.8,
            }}>
                <Icon sx={{
                    fontSize: 16,
                    color: isEnabled ? retroColors.accent : retroColors.muted,
                }} />
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                    color: isEnabled ? retroColors.accent : retroColors.muted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                }}>
                    {label}
                </Typography>
                {!appData?.enabled && (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        color: retroColors.muted,
                        ml: 'auto',
                        letterSpacing: '0.05em',
                    }}>
                        Not enabled
                    </Typography>
                )}
                {appData?.enabled && appData?.scenarios?.length === 0 && (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.6rem',
                        color: retroColors.muted,
                        ml: 'auto',
                        letterSpacing: '0.05em',
                    }}>
                        No scenarios
                    </Typography>
                )}
            </Box>

            {/* Scenario dropdown */}
            {isEnabled && (
                <FormControl fullWidth size="small" sx={fieldSx}>
                    <InputLabel>Scenario</InputLabel>
                    <Select
                        value={selectedId || ''}
                        label="Scenario"
                        onChange={(e) => onChange(appKey, e.target.value || null)}
                        disabled={isDisabledState}
                        MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                        renderValue={(value) => {
                            const scenario = appData.scenarios.find(s => s.id === value);
                            return scenario ? scenario.name : '';
                        }}
                    >
                        <MenuItem value="" sx={menuItemSx}>
                            <em>None</em>
                        </MenuItem>
                        {appData.scenarios.map((s) => (
                            <MenuItem key={s.id} value={s.id} sx={menuItemSx}>
                                <Box>
                                    <Typography sx={{
                                        fontFamily: vortxTypography.mono,
                                        fontSize: '0.8rem',
                                        color: vortxColors.white,
                                    }}>
                                        {s.name}
                                        {s.has_results && (
                                            <Box component="span" sx={{
                                                ml: 1,
                                                fontSize: '0.55rem',
                                                color: retroColors.accent,
                                                verticalAlign: 'middle',
                                            }}>
                                                HAS RESULTS
                                            </Box>
                                        )}
                                    </Typography>
                                    {s.equipment_summary && (
                                        <Typography sx={{
                                            fontFamily: vortxTypography.mono,
                                            fontSize: '0.65rem',
                                            color: retroColors.dimmed,
                                            mt: 0.2,
                                        }}>
                                            {s.equipment_summary}
                                        </Typography>
                                    )}
                                </Box>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )}
        </Box>
    );
}

const STATUS_COLORS = {
    running: '#44ff88',
    booting: vortxColors.gold,
    stopped: retroColors.muted,
    completed: retroColors.muted,
    killed: vortxColors.error,
};

function formatRelativeTime(isoString) {
    if (!isoString) return '—';
    const diff = Date.now() - new Date(isoString).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

function formatDuration(config) {
    if (!config?.duration_days) return '—';
    const d = config.duration_days;
    if (d <= 7) return `${d}d`;
    if (d <= 31) return `${Math.round(d / 30)}m`;
    return `${Math.round(d / 365)}y`;
}

const cellSx = {
    fontFamily: vortxTypography.mono,
    fontSize: '0.75rem',
    color: vortxColors.white,
    borderBottom: `1px solid ${vortxColors.teal}22`,
    py: 1,
};

function SimulationRunsTable({ runs, runsLoading, onKill }) {
    return (
        <Box sx={{ mt: 4, px: 3, maxWidth: 800, mx: 'auto' }}>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.65rem',
                fontWeight: 600,
                color: vortxColors.teal,
                textTransform: 'uppercase',
                letterSpacing: '0.12em',
                mb: 1.5,
            }}>
                Recent Runs
            </Typography>

            {runsLoading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
                    <CircularProgress size={14} sx={{ color: retroColors.accent }} />
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.7rem',
                        color: retroColors.dimmed,
                    }}>
                        Loading runs...
                    </Typography>
                </Box>
            ) : runs.length === 0 ? (
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.7rem',
                    color: retroColors.dimmed,
                    py: 2,
                }}>
                    No simulation runs yet
                </Typography>
            ) : (
                <ProjectTableContainer>
                    <ProjectTableHead>
                        <ProjectTableHeaderCell>Status</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Started</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Duration</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Speed</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell>Progress</ProjectTableHeaderCell>
                        <ProjectTableHeaderCell align="right" />
                    </ProjectTableHead>
                    <ProjectTableBody>
                        {runs.map((run) => {
                            const color = STATUS_COLORS[run.status] || retroColors.muted;
                            const isActive = run.status === 'running' || run.status === 'booting';
                            return (
                                <TableRow key={run.sim_id} sx={{
                                    '&:hover': { backgroundColor: `${vortxColors.teal}0a` },
                                }}>
                                    <TableCell sx={cellSx}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                                            <FiberManualRecord sx={{ fontSize: 8, color }} />
                                            <span style={{ color, textTransform: 'uppercase', fontSize: '0.7rem' }}>
                                                {run.status}
                                            </span>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        {formatRelativeTime(run.started_at)}
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        {formatDuration(run.config)}
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        {run.config?.speed ? `${run.config.speed}x` : '—'}
                                    </TableCell>
                                    <TableCell sx={cellSx}>
                                        {run.checkpoint?.day ? `Day ${run.checkpoint.day}` : '—'}
                                    </TableCell>
                                    <TableCell sx={{ ...cellSx, textAlign: 'right' }}>
                                        {isActive && (
                                            <Button
                                                size="small"
                                                onClick={() => onKill(run.sim_id)}
                                                sx={{
                                                    fontFamily: vortxTypography.mono,
                                                    fontSize: '0.65rem',
                                                    fontWeight: 700,
                                                    color: vortxColors.error,
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.08em',
                                                    minWidth: 0,
                                                    px: 1.5,
                                                    py: 0.3,
                                                    border: `1px solid ${vortxColors.error}44`,
                                                    '&:hover': {
                                                        backgroundColor: `${vortxColors.error}11`,
                                                        borderColor: vortxColors.error,
                                                    },
                                                }}
                                            >
                                                Kill
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </ProjectTableBody>
                </ProjectTableContainer>
            )}
        </Box>
    );
}

/**
 * SimulationSetup - Pre-start configuration screen.
 *
 * Shows scenarios grouped by app (Site DNA, Flux EV, Flux COLD) with
 * per-app dropdowns, duration, and speed selection.
 *
 * @param {{
 *   appScenarios: object,
 *   projectName: string|undefined,
 *   onStart: function({ scenarioIds: object, duration: string, speed: number }),
 *   loading: boolean,
 *   runs: Array,
 *   runsLoading: boolean,
 *   onKill: function(simId: string),
 * }} props
 */
export default function SimulationSetup({ appScenarios, projectName, onStart, loading, error, runs, runsLoading, onKill }) {
    const [scenarioIds, setScenarioIds] = useState({});
    const [duration, setDuration] = useState('1w');
    const [speed, setSpeed] = useState(10);
    const [startPeriod, setStartPeriod] = useState(0); // week index or month index

    // Initialize selections from default_scenario_id when data loads
    useEffect(() => {
        if (!appScenarios) return;

        const defaults = {};
        for (const appKey of APP_ORDER) {
            const appData = appScenarios[appKey];
            if (appData?.enabled && appData.default_scenario_id) {
                defaults[appKey] = appData.default_scenario_id;
            }
        }
        setScenarioIds(prev => {
            // Only set defaults if not already selected by user
            const merged = { ...defaults };
            for (const [key, value] of Object.entries(prev)) {
                if (value) merged[key] = value;
            }
            return merged;
        });
    }, [appScenarios]);

    const handleScenarioChange = (appKey, scenarioId) => {
        setScenarioIds(prev => ({
            ...prev,
            [appKey]: scenarioId,
        }));
    };

    // At least one scenario must be selected
    const hasAnySelection = useMemo(() => {
        return Object.values(scenarioIds).some(id => id);
    }, [scenarioIds]);

    // Compute the start step based on duration type and selected period
    const startStep = useMemo(() => {
        if (duration === '1w') {
            return WEEK_OPTIONS[startPeriod]?.startStep || 0;
        }
        if (duration === '1m') {
            // Month start: sum days in preceding months × 96 steps/day
            const daysBeforeMonth = [0, 31, 59, 90, 120, 151, 181, 212, 243, 273, 304, 334];
            return (daysBeforeMonth[startPeriod] || 0) * 96;
        }
        return 0; // 1y always starts Jan 1
    }, [duration, startPeriod]);

    const handleStart = () => {
        if (!hasAnySelection) return;
        const cleanIds = {};
        for (const [appKey, id] of Object.entries(scenarioIds)) {
            if (id) cleanIds[appKey] = id;
        }
        onStart({ scenarioIds: cleanIds, duration, speed, startStep });
    };

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'auto',
        }}>
          <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 3,
              flexShrink: 0,
          }}>
            <Box sx={{
                width: 480,
                backgroundColor: retroColors.surface,
                border: `1px solid ${vortxColors.teal}44`,
                borderRadius: 1,
                p: 3,
                boxShadow: `0 0 40px ${vortxColors.teal}11`,
            }}>
                {/* Header */}
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    color: retroColors.accent,
                    textTransform: 'uppercase',
                    letterSpacing: '0.15em',
                    mb: 0.5,
                }}>
                    Digital Twin
                </Typography>
                <Typography sx={{
                    fontFamily: vortxTypography.mono,
                    fontSize: '0.65rem',
                    color: retroColors.dimmed,
                    mb: 1,
                    letterSpacing: '0.05em',
                }}>
                    Configure and launch a real-time site simulation
                </Typography>

                {/* Project name */}
                {projectName && (
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: vortxColors.gold,
                        mb: 2.5,
                        letterSpacing: '0.04em',
                    }}>
                        {projectName}
                    </Typography>
                )}
                {!projectName && !loading && (
                    <Box sx={{ mb: 2.5 }} />
                )}

                {/* Loading state */}
                {loading && !appScenarios ? (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        py: 4,
                        gap: 1.5,
                    }}>
                        <CircularProgress size={18} sx={{ color: retroColors.accent }} />
                        <Typography sx={{
                            fontFamily: vortxTypography.mono,
                            fontSize: '0.7rem',
                            color: retroColors.dimmed,
                            letterSpacing: '0.05em',
                        }}>
                            Loading scenarios...
                        </Typography>
                    </Box>
                ) : (
                    <>
                        {/* App scenario selectors */}
                        <Box sx={{
                            mb: 2.5,
                            pb: 2,
                            borderBottom: `1px solid ${vortxColors.teal}22`,
                        }}>
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.6rem',
                                fontWeight: 600,
                                color: retroColors.muted,
                                textTransform: 'uppercase',
                                letterSpacing: '0.12em',
                                mb: 1.5,
                            }}>
                                App Scenarios
                            </Typography>

                            {APP_ORDER.map((appKey) => (
                                <AppScenarioSelector
                                    key={appKey}
                                    appKey={appKey}
                                    appData={appScenarios?.[appKey]}
                                    selectedId={scenarioIds[appKey]}
                                    onChange={handleScenarioChange}
                                    disabled={loading}
                                />
                            ))}
                        </Box>

                        {/* Duration + Start Period */}
                        <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                            <FormControl size="small" sx={{ ...fieldSx, minWidth: 130 }}>
                                <InputLabel>Duration</InputLabel>
                                <Select
                                    value={duration}
                                    label="Duration"
                                    onChange={(e) => {
                                        setDuration(e.target.value);
                                        setStartPeriod(0);
                                    }}
                                    MenuProps={{ PaperProps: { sx: menuPaperSx } }}
                                >
                                    {DURATION_OPTIONS.map((d) => (
                                        <MenuItem key={d.value} value={d.value} sx={menuItemSx}>
                                            {d.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            {/* Start period picker — week or month depending on duration */}
                            {duration !== '1y' && (
                                <FormControl size="small" sx={{ ...fieldSx, flex: 1 }}>
                                    <InputLabel>Start</InputLabel>
                                    <Select
                                        value={startPeriod}
                                        label="Start"
                                        onChange={(e) => setStartPeriod(e.target.value)}
                                        MenuProps={{ PaperProps: { sx: { ...menuPaperSx, maxHeight: 300 } } }}
                                    >
                                        {duration === '1w'
                                            ? WEEK_OPTIONS.map((w) => (
                                                <MenuItem key={w.value} value={w.value} sx={menuItemSx}>
                                                    {w.label}
                                                </MenuItem>
                                            ))
                                            : MONTHS.map((m, i) => (
                                                <MenuItem key={i} value={i} sx={menuItemSx}>
                                                    {m}
                                                </MenuItem>
                                            ))
                                        }
                                    </Select>
                                </FormControl>
                            )}
                        </Box>

                        {/* Boot error */}
                        {error && (
                            <Typography sx={{
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.7rem',
                                color: vortxColors.error,
                                mb: 1.5,
                                p: 1,
                                backgroundColor: `${vortxColors.error}11`,
                                border: `1px solid ${vortxColors.error}33`,
                                borderRadius: 0.5,
                            }}>
                                {error}
                            </Typography>
                        )}

                        {/* Start Button */}
                        <Button
                            onClick={handleStart}
                            disabled={!hasAnySelection || loading}
                            fullWidth
                            variant="contained"
                            startIcon={<PlayArrow />}
                            sx={{
                                backgroundColor: vortxColors.gold,
                                color: retroColors.bg,
                                fontFamily: vortxTypography.mono,
                                fontSize: '0.8rem',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                                letterSpacing: '0.1em',
                                py: 1.2,
                                '&:hover': {
                                    backgroundColor: vortxColors.goldHover,
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: `${vortxColors.gold}44`,
                                    color: `${retroColors.bg}88`,
                                },
                            }}
                        >
                            {loading ? 'Initializing...' : 'Start Simulation'}
                        </Button>
                    </>
                )}
            </Box>
          </Box>

          <SimulationRunsTable runs={runs || []} runsLoading={runsLoading} onKill={onKill} />
        </Box>
    );
}
